import { useId } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "../../../components/ui/alert";
import { Checkbox } from "../../../components/ui/checkbox";
import { cn } from "../../../lib/utils";

/**
 * A disclaimer the customer has to accept before continuing: a notice with a
 * title and body, and an "I have read and agree" row under it. Every
 * disclaimer on the Repair or Cash step uses this, so they look alike.
 *
 * Unaccepted it uses the warning colours from validationNotices.jsx, so it
 * reads as the thing holding Continue; accepted it settles to neutral with a
 * check, so a glance down the page shows what is still left.
 */
function ClaimDisclaimer({ title, checked, onCheckedChange, children }) {
  const { t } = useTranslation();
  const id = useId();
  const titleId = `${id}-title`;
  const checkboxId = `${id}-accept`;
  const Icon = checked ? CheckCircle2 : AlertTriangle;

  return (
    <Alert
      role="group"
      aria-labelledby={titleId}
      data-state={checked ? "accepted" : "pending"}
      className={cn(
        "mt-4 flex-col gap-0 overflow-hidden p-0",
        "transition-[background-color,border-color] duration-200 ease-out",
        checked
          ? "border-[#DDE2E4] bg-[#F8FAFB]"
          : "border-[#EFD5A3] bg-[#FEFAF0]"
      )}
    >
      <div className="flex w-full items-start gap-2.5 px-4 pt-3.5 pb-3">
        <Icon
          aria-hidden="true"
          className={cn(
            "mt-1 h-4 w-4 shrink-0 transition-colors duration-200 ease-out",
            checked ? "text-[#0A7CAE]" : "text-[#B8741A]"
          )}
        />
        <div className="min-w-0 space-y-1.5">
          <AlertTitle id={titleId} className="text-base leading-6 text-[#1A2024]">
            {title}
          </AlertTitle>
          <AlertDescription
            className={cn(
              "space-y-2 text-sm leading-[22px] transition-colors duration-200 ease-out",
              checked ? "text-[#48535B]" : "text-[#5E4B2B]"
            )}
          >
            {children}
          </AlertDescription>
        </div>
      </div>
      {/* The one thing on the card to act on, so it gets its own white strip,
          a full-size checkbox and body-weight text rather than reading as one
          more line of the fine print above it. */}
      <label
        htmlFor={checkboxId}
        className={cn(
          "flex min-h-12 w-full cursor-pointer select-none items-center gap-3 border-t px-4 py-3",
          "text-sm font-semibold text-[#1A2024]",
          "transition-[background-color,border-color] duration-150 ease-out",
          checked
            ? "border-[#DDE2E4] bg-transparent [@media(hover:hover)]:hover:bg-black/[0.03]"
            : "border-[#EFD5A3] bg-white [@media(hover:hover)]:hover:bg-[#FFFDF8]",
          "active:bg-black/[0.04]"
        )}
      >
        <Checkbox
          id={checkboxId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className={cn(
            "h-5 w-5 rounded-[5px] border-2 bg-white shadow-none",
            "transition-[background-color,border-color,transform] duration-150 ease-out active:scale-[0.92]",
            "[&>svg]:h-3.5 [&>svg]:w-3.5",
            !checked && "border-[#0A7CAE]"
          )}
        />
        {t("motorInsurance.fileAClaimFlow.soilidarityUserRepairOrCashPage.disclaimer_accept")}
      </label>
    </Alert>
  );
}

export default ClaimDisclaimer;
